import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Flame,
  Sparkles,
  Sliders,
  Link as LinkIcon,
  KeyRound,
  Cpu,
  Copy,
  Check,
  Eraser,
  MessagesSquare,
  Languages,
  LogOut,
  ImagePlus,
  X,
  ShieldAlert,
  Settings2,
  Gauge,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import {
  DEFAULT_PERSONA,
  DEFAULT_FUNNEL_LINK,
  OPENROUTER_MODELS,
  DEFAULT_MODEL,
  DEFAULT_ABOUT_ME,
  DEFAULT_BOUNDARIES,
  DEFAULT_TRANSITION_MESSAGE,
  DEFAULT_FUNNEL_RULES,
  DEFAULT_REPLY_SETTINGS,
} from "@/lib/defaults";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import ConversationsPanel from "@/components/ConversationsPanel";
import FunnelPanel from "@/components/FunnelPanel";

function parseHistoryText(text) {
  if (!text || !text.trim()) return [];
  const lines = text.split(/\r?\n/);
  const turns = [];
  for (const line of lines) {
    const m = /^\s*(THEM|ME)\s*:\s*(.+)$/i.exec(line);
    if (m) {
      turns.push({
        role: m[1].toUpperCase() === "THEM" ? "them" : "me",
        text: m[2].trim(),
      });
    }
  }
  return turns;
}

function heatBucket(heat) {
  if (heat >= 60) return "hot";
  if (heat >= 30) return "warm";
  return "cold";
}

export default function Dashboard({ lang, onToggleLang, session }) {
  const t = useT(lang);

  // Persona / settings state (source of truth = Supabase settings table)
  const [personaName, setPersonaName] = useState(DEFAULT_PERSONA.name);
  const [tone, setTone] = useState(DEFAULT_PERSONA.toneStyle);
  const [doRules, setDoRules] = useState(DEFAULT_PERSONA.doRules);
  const [dontRules, setDontRules] = useState(DEFAULT_PERSONA.dontRules);
  const [languages, setLanguages] = useState(DEFAULT_PERSONA.languages);
  const [funnelLink, setFunnelLink] = useState(DEFAULT_FUNNEL_LINK);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState(DEFAULT_MODEL);

  // Phase 3 persona/rules state
  const [age, setAge] = useState("");
  const [aboutMe, setAboutMe] = useState(DEFAULT_ABOUT_ME);
  const [boundaries, setBoundaries] = useState(DEFAULT_BOUNDARIES);
  const [transitionMessage, setTransitionMessage] = useState(DEFAULT_TRANSITION_MESSAGE);
  const [funnelRules, setFunnelRules] = useState(DEFAULT_FUNNEL_RULES);
  const [replySettings, setReplySettings] = useState(DEFAULT_REPLY_SETTINGS);

  // Image
  const [imageDataUrl, setImageDataUrl] = useState("");
  const [imageName, setImageName] = useState("");

  // Phase 4: profile status (soft gate)
  const [profileStatus, setProfileStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me");
        if (!cancelled) setProfileStatus(data?.status || null);
      } catch {
        /* silently ignore — pre-migration or logout */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Conversations
  const [activeConvId, setActiveConvId] = useState(null);
  const [convRefresh, setConvRefresh] = useState(0);

  // Funnel slug (for "Use as AI funnel link" button)
  const [funnelSlug, setFunnelSlug] = useState(null);

  // Inbox / output
  const [incoming, setIncoming] = useState("");
  const [historyText, setHistoryText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Load settings from Supabase on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (cancelled || !data) return;
        setPersonaName(data.persona_name ?? DEFAULT_PERSONA.name);
        setTone(data.tone ?? DEFAULT_PERSONA.toneStyle);
        setDoRules(data.do_rules ?? DEFAULT_PERSONA.doRules);
        setDontRules(data.dont_rules ?? DEFAULT_PERSONA.dontRules);
        setLanguages(data.languages ?? DEFAULT_PERSONA.languages);
        setFunnelLink(data.funnel_link ?? DEFAULT_FUNNEL_LINK);
        setModel(data.model ?? DEFAULT_MODEL);
        setApiKey(data.openrouter_key ?? "");
        // Phase 3 hydration
        setAge(data.age == null ? "" : String(data.age));
        setAboutMe(data.about_me ?? DEFAULT_ABOUT_ME);
        setBoundaries(data.boundaries ?? DEFAULT_BOUNDARIES);
        setTransitionMessage(data.transition_message ?? DEFAULT_TRANSITION_MESSAGE);
        if (data.funnel_rules && Object.keys(data.funnel_rules).length) {
          setFunnelRules({ ...DEFAULT_FUNNEL_RULES, ...data.funnel_rules });
        }
        if (data.reply_settings && Object.keys(data.reply_settings).length) {
          setReplySettings({ ...DEFAULT_REPLY_SETTINGS, ...data.reply_settings });
        }
      } catch (e) {
        // Suppress transient errors during logout (401 from a just-cleared session).
        if (cancelled || e?.response?.status === 401) return;
        toast.error(
          `${t("settingsLoadError")}: ${e?.response?.data?.detail || e.message}`,
        );
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced auto-save to Supabase whenever settings change (after load)
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!settingsLoaded) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put("/settings", {
          persona_name: personaName,
          tone,
          do_rules: doRules,
          dont_rules: dontRules,
          funnel_link: funnelLink,
          languages,
          model,
          openrouter_key: apiKey || null,
          age: age === "" ? null : Number(age),
          about_me: aboutMe,
          boundaries,
          transition_message: transitionMessage,
          funnel_rules: funnelRules,
          reply_settings: replySettings,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || String(e));
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    personaName,
    tone,
    doRules,
    dontRules,
    funnelLink,
    languages,
    model,
    apiKey,
    settingsLoaded,
    age,
    aboutMe,
    boundaries,
    transitionMessage,
    funnelRules,
    replySettings,
  ]);

  const handleSuggest = async () => {
    if (!incoming.trim() && !imageDataUrl) {
      toast.error(t("errorNoMessage"));
      return;
    }
    setLoading(true);
    setResult(null);
    setCopied(false);
    try {
      const body = {
        incomingMessage: incoming,
        history: parseHistoryText(historyText),
        persona: {
          name: personaName,
          toneStyle: tone,
          doRules,
          dontRules,
          languages,
        },
        funnelLink,
        openrouterApiKey: apiKey || undefined,
        model,
        conversationId: activeConvId || undefined,
        persist: !!activeConvId,
        aboutMe,
        boundaries,
        transitionMessage,
        funnelRules,
        replySettings,
        imageDataUrl: imageDataUrl || undefined,
      };
      const { data } = await api.post("/suggest-reply", body);
      setResult(data);
      if (activeConvId) {
        setConvRefresh((n) => n + 1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t("errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.suggestedReply) return;
    try {
      await navigator.clipboard.writeText(result.suggestedReply);
      setCopied(true);
      toast.success(t("successCopied"));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("errorGeneric"));
    }
  };

  const handleClear = () => {
    setIncoming("");
    setHistoryText("");
    setResult(null);
    setCopied(false);
    setImageDataUrl("");
    setImageName("");
  };

  const handleImageSelect = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("errorGeneric"));
      return;
    }
    // limit ~5MB
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageDataUrl(String(reader.result || ""));
      setImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const heat = result?.heat ?? 0;
  const bucket = heatBucket(heat);
  const isHot = !!result?.isHot;

  const activeConvLabel = useMemo(() => {
    return activeConvId ? activeConvId.slice(0, 8) : null;
  }, [activeConvId]);

  return (
    <div className="cockpit-bg min-h-screen w-full">
      {/* Header */}
      <header
        className="border-b border-white/5 backdrop-blur"
        data-testid="app-header"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#FF006E]/15 border border-[#FF006E]/40 flex items-center justify-center">
              <Flame size={18} strokeWidth={1.75} className="text-[#FF006E]" />
            </div>
            <div>
              <h1
                className="font-display text-xl md:text-2xl font-semibold tracking-tight"
                data-testid="app-title"
              >
                {t("appTitle")}
              </h1>
              <p className="text-xs text-zinc-400">{t("appSubtitle")}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              className="hidden md:inline text-[10px] text-zinc-500 tracking-[0.15em] uppercase"
              data-testid="signed-in-as"
            >
              {t("signedInAs")}: {session?.user?.email}
            </span>
            {saving && (
              <span
                className="text-[10px] text-zinc-500 uppercase tracking-widest"
                data-testid="saving-indicator"
              >
                {t("savingSettings")}
              </span>
            )}
            <div className="pill-group" data-testid="lang-toggle">
              <button
                className={`pill-btn ${lang === "sk" ? "active" : ""}`}
                onClick={() => onToggleLang("sk")}
                data-testid="lang-toggle-sk"
              >
                SK
              </button>
              <button
                className={`pill-btn ${lang === "en" ? "active" : ""}`}
                onClick={() => onToggleLang("en")}
                data-testid="lang-toggle-en"
              >
                EN
              </button>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={handleLogout}
              className="text-zinc-300 hover:text-white hover:bg-white/5"
              data-testid="logout-btn"
            >
              <LogOut size={14} strokeWidth={1.7} className="mr-1.5" />
              {t("logoutBtn")}
            </Button>
          </div>
        </div>
      </header>

      {/* Main grid */}
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-8">
        {profileStatus && profileStatus !== "approved" && profileStatus !== "unknown" && (
          <div
            className={`surface p-5 md:p-6 mb-6 border ${profileStatus === "banned" ? "border-[#FF006E]/50 bg-[#FF006E]/5" : "border-[#FFBE0B]/40 bg-[#FFBE0B]/5"}`}
            data-testid="pending-banner"
          >
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${profileStatus === "banned" ? "bg-[#FF006E]/15" : "bg-[#FFBE0B]/15"}`}>
                <Flame size={18} strokeWidth={1.7} className={profileStatus === "banned" ? "text-[#FF006E]" : "text-[#FFBE0B]"} />
              </div>
              <div>
                <p className={`font-display text-base font-semibold ${profileStatus === "banned" ? "text-[#FF006E]" : "text-[#FFBE0B]"}`}>
                  {profileStatus === "banned" ? t("bannedBannerTitle") : t("pendingBannerTitle")}
                </p>
                <p className="text-zinc-300 text-sm mt-1 leading-relaxed">
                  {profileStatus === "banned" ? t("bannedBannerBody") : t("pendingBannerBody")}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT — Configuration + Conversations */}
          <section className="lg:col-span-4 flex flex-col gap-6">
            <div className="surface p-6 md:p-7" data-testid="config-panel">
              <div className="flex items-center gap-2 mb-1">
                <Sliders size={14} strokeWidth={1.5} className="text-[#FF006E]" />
                <span className="overline">{t("configOverline")}</span>
              </div>
              <h2 className="font-display text-lg font-semibold mb-6">
                {t("configTitle")}
              </h2>

              <div className="space-y-5">
                <FieldBlock label={t("personaName")}>
                  <Input
                    data-testid="persona-name-input"
                    value={personaName}
                    onChange={(e) => setPersonaName(e.target.value)}
                    placeholder={t("personaNamePh")}
                    className="field-input bg-transparent"
                  />
                </FieldBlock>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <FieldBlock label={t("ageLabel")}>
                      <Input
                        data-testid="age-input"
                        value={age}
                        onChange={(e) => setAge(e.target.value.replace(/\D/g, "").slice(0, 3))}
                        placeholder="26"
                        className="field-input bg-transparent"
                        inputMode="numeric"
                      />
                    </FieldBlock>
                  </div>
                  <div className="col-span-2">
                    <FieldBlock
                      label={t("languagesLabel")}
                      icon={<Languages size={13} strokeWidth={1.6} />}
                    >
                      <Input
                        data-testid="languages-input"
                        value={languages}
                        onChange={(e) => setLanguages(e.target.value)}
                        placeholder={t("languagesPh")}
                        className="field-input bg-transparent"
                      />
                    </FieldBlock>
                  </div>
                </div>

                <FieldBlock label={t("aboutMe")}>
                  <Textarea
                    data-testid="about-me-input"
                    value={aboutMe}
                    onChange={(e) => setAboutMe(e.target.value)}
                    placeholder={t("aboutMePh")}
                    className="field-textarea bg-transparent min-h-[100px]"
                  />
                  <p className="text-[11px] text-zinc-500 mt-2">{t("aboutMeHint")}</p>
                </FieldBlock>

                <FieldBlock label={t("toneStyle")}>
                  <Textarea
                    data-testid="tone-style-input"
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    placeholder={t("toneStylePh")}
                    className="field-textarea bg-transparent min-h-[100px]"
                  />
                </FieldBlock>

                <FieldBlock label={t("doRules")}>
                  <Textarea
                    data-testid="do-rules-input"
                    value={doRules}
                    onChange={(e) => setDoRules(e.target.value)}
                    placeholder={t("doRulesPh")}
                    className="field-textarea bg-transparent min-h-[90px]"
                  />
                </FieldBlock>

                <FieldBlock label={t("dontRules")}>
                  <Textarea
                    data-testid="dont-rules-input"
                    value={dontRules}
                    onChange={(e) => setDontRules(e.target.value)}
                    placeholder={t("dontRulesPh")}
                    className="field-textarea bg-transparent min-h-[90px]"
                  />
                </FieldBlock>

                <FieldBlock label={t("boundariesLabel")} icon={<ShieldAlert size={13} strokeWidth={1.6} />}>
                  <Textarea
                    data-testid="boundaries-input"
                    value={boundaries}
                    onChange={(e) => setBoundaries(e.target.value)}
                    placeholder={t("boundariesPh")}
                    className="field-textarea bg-transparent min-h-[90px]"
                  />
                </FieldBlock>

                <FieldBlock
                  label={t("funnelLink")}
                  icon={<LinkIcon size={13} strokeWidth={1.6} />}
                >
                  <Input
                    data-testid="funnel-link-input"
                    value={funnelLink}
                    onChange={(e) => setFunnelLink(e.target.value)}
                    placeholder={t("funnelLinkPh")}
                    className="field-input bg-transparent"
                  />
                  {funnelSlug && (
                    <button
                      type="button"
                      onClick={() => {
                        const url = `${window.location.origin}/f/${funnelSlug}`;
                        setFunnelLink(url);
                        toast.success(t("aiFunnelSet"));
                      }}
                      className="mt-2 text-[11px] text-[#FF006E] hover:text-[#ff338b] uppercase tracking-widest inline-flex items-center gap-1"
                      data-testid="use-as-ai-funnel-btn"
                    >
                      → {t("setAsAiFunnel")} (/f/{funnelSlug})
                    </button>
                  )}
                </FieldBlock>

                <FieldBlock label={t("transitionLabel")}>
                  <Textarea
                    data-testid="transition-message-input"
                    value={transitionMessage}
                    onChange={(e) => setTransitionMessage(e.target.value)}
                    placeholder={t("transitionPh")}
                    className="field-textarea bg-transparent min-h-[70px]"
                  />
                </FieldBlock>

                <Separator className="bg-white/5" />

                <div className="flex items-center gap-2 mb-1">
                  <Gauge size={14} strokeWidth={1.5} className="text-[#FF006E]" />
                  <span className="overline">{t("rulesOverline")}</span>
                </div>
                <h3 className="font-display text-base font-semibold mb-3">
                  {t("rulesTitle")}
                </h3>

                <div className="grid grid-cols-2 gap-3">
                  <FieldBlock label={t("ruleMinMessages")}>
                    <Input
                      data-testid="rule-min-messages-input"
                      value={String(funnelRules.min_messages_before_link ?? 0)}
                      onChange={(e) =>
                        setFunnelRules((r) => ({
                          ...r,
                          min_messages_before_link: Number(e.target.value.replace(/\D/g, "") || 0),
                        }))
                      }
                      className="field-input bg-transparent"
                      inputMode="numeric"
                    />
                  </FieldBlock>
                  <FieldBlock label={t("ruleHeatThreshold")}>
                    <Input
                      data-testid="rule-heat-threshold-input"
                      value={String(funnelRules.heat_threshold ?? 60)}
                      onChange={(e) => {
                        const v = Math.max(0, Math.min(100, Number(e.target.value.replace(/\D/g, "") || 0)));
                        setFunnelRules((r) => ({ ...r, heat_threshold: v }));
                      }}
                      className="field-input bg-transparent"
                      inputMode="numeric"
                    />
                  </FieldBlock>
                  <FieldBlock label={t("ruleCooldown")}>
                    <Input
                      data-testid="rule-cooldown-input"
                      value={String(funnelRules.cooldown_messages ?? 0)}
                      onChange={(e) =>
                        setFunnelRules((r) => ({
                          ...r,
                          cooldown_messages: Number(e.target.value.replace(/\D/g, "") || 0),
                        }))
                      }
                      className="field-input bg-transparent"
                      inputMode="numeric"
                    />
                  </FieldBlock>
                  <FieldBlock label={t("ruleIntentTriggers")}>
                    <Input
                      data-testid="rule-triggers-input"
                      value={(funnelRules.intent_triggers || []).join(", ")}
                      onChange={(e) =>
                        setFunnelRules((r) => ({
                          ...r,
                          intent_triggers: e.target.value
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean),
                        }))
                      }
                      placeholder={t("ruleIntentTriggersPh")}
                      className="field-input bg-transparent"
                    />
                  </FieldBlock>
                </div>

                <Separator className="bg-white/5" />

                <div className="flex items-center gap-2 mb-1">
                  <Settings2 size={14} strokeWidth={1.5} className="text-[#FF006E]" />
                  <span className="overline">{t("replyOverline")}</span>
                </div>
                <h3 className="font-display text-base font-semibold mb-3">
                  {t("replyTitleSection")}
                </h3>

                <div className="grid grid-cols-2 gap-3">
                  <FieldBlock label={t("toneIntensity")}>
                    <Select
                      value={replySettings.tone_intensity}
                      onValueChange={(v) => setReplySettings((s) => ({ ...s, tone_intensity: v }))}
                    >
                      <SelectTrigger data-testid="tone-intensity-select" className="field-input bg-transparent">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subtle">{t("toneSubtle")}</SelectItem>
                        <SelectItem value="balanced">{t("toneBalanced")}</SelectItem>
                        <SelectItem value="bold">{t("toneBold")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldBlock>
                  <FieldBlock label={t("replyLength")}>
                    <Select
                      value={replySettings.reply_length}
                      onValueChange={(v) => setReplySettings((s) => ({ ...s, reply_length: v }))}
                    >
                      <SelectTrigger data-testid="reply-length-select" className="field-input bg-transparent">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">{t("lenShort")}</SelectItem>
                        <SelectItem value="medium">{t("lenMedium")}</SelectItem>
                        <SelectItem value="long">{t("lenLong")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldBlock>
                  <FieldBlock label={t("emojiLevel")}>
                    <Select
                      value={replySettings.emoji_level}
                      onValueChange={(v) => setReplySettings((s) => ({ ...s, emoji_level: v }))}
                    >
                      <SelectTrigger data-testid="emoji-level-select" className="field-input bg-transparent">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("emojiNone")}</SelectItem>
                        <SelectItem value="light">{t("emojiLight")}</SelectItem>
                        <SelectItem value="moderate">{t("emojiModerate")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldBlock>
                  <FieldBlock label={t("languageMode")}>
                    <Select
                      value={replySettings.language_mode}
                      onValueChange={(v) => setReplySettings((s) => ({ ...s, language_mode: v }))}
                    >
                      <SelectTrigger data-testid="language-mode-select" className="field-input bg-transparent">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mirror">{t("langMirror")}</SelectItem>
                        <SelectItem value="force_sk">{t("langForceSk")}</SelectItem>
                        <SelectItem value="force_en">{t("langForceEn")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldBlock>
                </div>

                <Separator className="bg-white/5" />

                <div className="flex items-center gap-2 mb-1">
                  <KeyRound size={14} strokeWidth={1.5} className="text-[#FF006E]" />
                  <span className="overline">{t("apiOverline")}</span>
                </div>

                <FieldBlock label={t("apiKey")}>
                  <Input
                    data-testid="api-key-input"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={t("apiKeyPh")}
                    className="field-input bg-transparent"
                    autoComplete="off"
                  />
                  <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                    {t("apiKeyHint")}
                  </p>
                </FieldBlock>

                <FieldBlock
                  label={t("model")}
                  icon={<Cpu size={13} strokeWidth={1.6} />}
                >
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger
                      data-testid="model-select"
                      className="field-input bg-transparent"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPENROUTER_MODELS.map((m) => (
                        <SelectItem
                          key={m.value}
                          value={m.value}
                          data-testid={`model-option-${m.value}`}
                        >
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldBlock>
              </div>
            </div>

            <ConversationsPanel
              lang={lang}
              activeId={activeConvId}
              onSelect={setActiveConvId}
              refreshTrigger={convRefresh}
            />

            <FunnelPanel lang={lang} onSlugChange={setFunnelSlug} />
          </section>

          {/* RIGHT — Inbox + Output */}
          <section className="lg:col-span-8 flex flex-col gap-6">
            {/* Inbox */}
            <div className="surface p-6 md:p-7" data-testid="inbox-panel">
              <div className="flex items-center gap-2 mb-1">
                <MessagesSquare
                  size={14}
                  strokeWidth={1.5}
                  className="text-[#FF006E]"
                />
                <span className="overline">{t("inboxOverline")}</span>
              </div>
              <div className="flex items-center justify-between gap-4 mb-6">
                <h2 className="font-display text-lg font-semibold">
                  {t("inboxTitle")}
                </h2>
                <span
                  className="text-[10px] uppercase tracking-widest text-zinc-500"
                  data-testid="active-conv-indicator"
                >
                  {activeConvId
                    ? `${t("convActive")}: ${activeConvLabel}`
                    : t("convNoneSelected")}
                </span>
              </div>

              <div className="space-y-5">
                <FieldBlock label={t("incomingLabel")}>
                  <Textarea
                    data-testid="incoming-message-input"
                    value={incoming}
                    onChange={(e) => setIncoming(e.target.value)}
                    placeholder={t("incomingPh")}
                    className="field-textarea bg-transparent min-h-[110px] text-[15px] leading-relaxed"
                  />
                </FieldBlock>

                <FieldBlock label={t("historyLabel")}>
                  <Textarea
                    data-testid="history-input"
                    value={historyText}
                    onChange={(e) => setHistoryText(e.target.value)}
                    placeholder={t("historyPh")}
                    className="field-textarea bg-transparent min-h-[110px] font-mono text-[13px]"
                  />
                  <p className="text-[11px] text-zinc-500 mt-2">
                    {t("historyHint")}
                  </p>
                </FieldBlock>

                {/* Image upload */}
                <div>
                  <Label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">
                    <ImagePlus size={13} strokeWidth={1.6} />
                    {t("imageAttach")}
                  </Label>
                  {imageDataUrl ? (
                    <div
                      className="surface-inner p-3 flex items-center justify-between gap-3"
                      data-testid="image-preview"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={imageDataUrl}
                          alt={imageName || "attached"}
                          className="w-14 h-14 rounded object-cover border border-white/10"
                        />
                        <div className="min-w-0">
                          <p className="text-sm truncate">{imageName || t("imageAttached")}</p>
                          <p className="text-[10px] text-zinc-500 uppercase tracking-widest">
                            {t("imageAttached")}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setImageDataUrl("");
                          setImageName("");
                        }}
                        className="text-zinc-500 hover:text-[#FF006E]"
                        data-testid="image-remove-btn"
                        aria-label="remove"
                      >
                        <X size={16} strokeWidth={1.7} />
                      </button>
                    </div>
                  ) : (
                    <label
                      className="surface-inner p-3 flex items-center justify-center gap-2 cursor-pointer border-dashed hover:border-[#FF006E]/50 transition-colors text-sm text-zinc-400"
                      data-testid="image-attach-label"
                    >
                      <ImagePlus size={15} strokeWidth={1.6} className="text-[#FF006E]" />
                      {t("imageAttachBtn")}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageSelect(e.target.files?.[0])}
                        data-testid="image-file-input"
                      />
                    </label>
                  )}
                  <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                    {t("imageAttachHint")}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 pt-1">
                  <button
                    type="button"
                    onClick={handleSuggest}
                    disabled={loading}
                    className="cta-primary rounded-md px-5 py-3 text-sm font-semibold uppercase tracking-[0.14em] inline-flex items-center gap-2"
                    data-testid="suggest-reply-btn"
                  >
                    {loading ? (
                      <>
                        <span className="typing">
                          <span />
                          <span />
                          <span />
                        </span>
                        <span>{t("suggestBtnLoading")}</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} strokeWidth={1.6} />
                        {t("suggestBtn")}
                      </>
                    )}
                  </button>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClear}
                    className="text-zinc-300 hover:text-white hover:bg-white/5"
                    data-testid="clear-btn"
                  >
                    <Eraser size={15} strokeWidth={1.6} className="mr-1.5" />
                    {t("clearBtn")}
                  </Button>
                </div>
              </div>
            </div>

            {/* Output */}
            <div
              className={`surface p-6 md:p-7 relative ${
                isHot ? "hot-glow" : ""
              }`}
              data-testid="output-panel"
            >
              <div className="flex items-center justify-between gap-4 mb-1">
                <div className="flex items-center gap-2">
                  <Sparkles
                    size={14}
                    strokeWidth={1.5}
                    className="text-[#FF006E]"
                  />
                  <span className="overline">{t("outputOverline")}</span>
                </div>

                {result && (
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="border-white/10 text-zinc-300 uppercase tracking-[0.15em] text-[10px]"
                      data-testid="detected-language-badge"
                    >
                      {t("detectedLang")}: {result.detectedLanguage}
                    </Badge>

                    <span
                      className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase border ${
                        bucket === "hot"
                          ? "hot-text border-[#FF006E]/60 bg-[#FF006E]/10"
                          : bucket === "warm"
                            ? "text-[#FFBE0B] border-[#FFBE0B]/50 bg-[#FFBE0B]/10 tracking-[0.25em]"
                            : "text-[#3A86FF] border-[#3A86FF]/50 bg-[#3A86FF]/10 tracking-[0.25em]"
                      }`}
                      data-testid="heat-badge"
                    >
                      {bucket === "hot"
                        ? t("hotBadge")
                        : bucket === "warm"
                          ? t("warmBadge")
                          : t("coldBadge")}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-4 mb-6">
                <h2 className="font-display text-lg font-semibold">
                  {t("replyTitle")}
                </h2>
                {result && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCopy}
                    className="text-zinc-300 hover:text-white hover:bg-white/5"
                    data-testid="copy-reply-btn"
                  >
                    {copied ? (
                      <>
                        <Check size={15} strokeWidth={1.8} className="mr-1.5" />
                        {t("copied")}
                      </>
                    ) : (
                      <>
                        <Copy size={15} strokeWidth={1.6} className="mr-1.5" />
                        {t("copyBtn")}
                      </>
                    )}
                  </Button>
                )}
              </div>

              {result ? (
                <>
                  {result.blocked ? (
                    <div
                      className="surface-inner p-5 border-[#FF006E]/60 bg-[#FF006E]/5"
                      data-testid="blocked-notice"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldAlert size={16} strokeWidth={1.7} className="text-[#FF006E]" />
                        <p className="font-semibold text-sm uppercase tracking-widest text-[#FF006E]">
                          {t("imageBlockedTitle")} · {result.blockReason || "safety"}
                        </p>
                      </div>
                      <p className="text-zinc-300 text-sm">{t("imageBlockedBody")}</p>
                    </div>
                  ) : (
                    <div
                      className="surface-inner p-5 text-[15px] leading-relaxed whitespace-pre-wrap"
                      data-testid="suggested-reply-text"
                    >
                      {result.suggestedReply || "—"}
                    </div>
                  )}

                  {!result.blocked && (result.linkReason || result.imageNote) && (
                    <div
                      className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest"
                      data-testid="reply-meta"
                    >
                      {result.linkReason && (
                        <span
                          className={`px-2 py-1 rounded border ${
                            result.includeLink
                              ? "text-[#FF006E] border-[#FF006E]/60 bg-[#FF006E]/10"
                              : "text-zinc-400 border-white/10"
                          }`}
                          data-testid="link-decision-badge"
                        >
                          {result.includeLink ? t("linkIncluded") : t("linkNotIncluded")} · {result.linkReason}
                        </span>
                      )}
                      {result.imageNote && (
                        <span
                          className="px-2 py-1 rounded border border-white/10 text-zinc-400"
                          data-testid="image-note-badge"
                        >
                          {t("imageNoteLabel")}: {result.imageNote}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="overline">{t("heatLabel")}</span>
                      <span
                        className="font-display text-2xl font-semibold"
                        data-testid="heat-score-value"
                      >
                        {heat}
                        <span className="text-zinc-500 text-sm ml-1">/ 100</span>
                      </span>
                    </div>
                    <div className="heat-track" data-testid="heat-gauge">
                      <div
                        className={`heat-fill ${bucket}`}
                        style={{ width: `${heat}%` }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div
                  className="surface-inner p-5 text-zinc-500 text-sm"
                  data-testid="empty-output-state"
                >
                  {loading ? (
                    <span className="typing">
                      <span />
                      <span />
                      <span />
                    </span>
                  ) : (
                    t("emptyOutput")
                  )}
                </div>
              )}
            </div>
          </section>
        </div>

        <footer className="mt-10 mb-4">
          <p
            className="text-[11px] text-zinc-500 tracking-[0.14em] uppercase"
            data-testid="app-footer"
          >
            {t("footerNote")}
          </p>
        </footer>
      </main>
    </div>
  );
}

function FieldBlock({ label, icon, children }) {
  return (
    <div>
      <Label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">
        {icon}
        {label}
      </Label>
      {children}
    </div>
  );
}
